/**
 * Hold Creation — server functions (M1).
 *
 * - listHoldCreationRules
 * - previewHoldCreationRule  (canonical Preview Impact; uses the SHARED
 *                             evaluator and buildOrderContext — the same
 *                             code runtime execution will use)
 * - activateHoldCreationIntent  (atomic upsert via RPC, used by AI and
 *                                manual Hold Creation authoring)
 *
 * Conditions go through `@/lib/policy-conditions` — no per-capability
 * evaluator. Actions go through this capability's `actions.ts` — no
 * generic "anything goes" action shape.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  evaluate,
  buildOrderContext,
  type Condition,
} from "@/lib/policy-conditions";
import { validateActions, HoldCreationActionZ } from "./actions";
import { recordOutcome } from "@/lib/policy-compiler/pipeline";

/* ── shared zod ───────────────────────────────────────────────────────── */

const ConditionLeafZ: z.ZodType = z.lazy(() =>
  z.object({
    type: z.literal("leaf"),
    field: z.string().min(1),
    op: z.string().min(1),
    value: z.unknown().optional(),
  }),
);
const ConditionGroupZ: z.ZodType = z.lazy(() =>
  z.object({
    type: z.enum(["and", "or", "not"]),
    children: z.array(ConditionZ),
  }),
);
const ConditionZ: z.ZodType = z.lazy(() =>
  z.union([ConditionLeafZ, ConditionGroupZ]),
);

const ScopeZ = z.discriminatedUnion("scope_level", [
  z.object({ scope_level: z.literal("tenant"), customer_id: z.null() }),
  z.object({ scope_level: z.literal("customer"), customer_id: z.string().uuid() }),
]);

export const HoldCreationRuleZ = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(600).optional().nullable(),
  condition_tree: ConditionZ,
  actions: z.array(HoldCreationActionZ).min(1).max(6),
  is_active: z.boolean().default(true),
  priority: z.number().int().min(1).max(1000).default(100),
});
export type HoldCreationRuleInput = z.infer<typeof HoldCreationRuleZ>;

/* ── list ─────────────────────────────────────────────────────────────── */

export const listHoldCreationRules = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ tenantId: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("hold_creation_rules")
      .select(
        "id, name, description, scope_level, customer_id, trigger_event, condition_tree, actions, is_active, priority, intent_id, intent_prompt, intent_summary, updated_at, customers(name)",
      )
      .eq("tenant_id", data.tenantId)
      .order("priority", { ascending: true })
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { rows: rows ?? [] };
  });

/* ── canonical Preview Impact ─────────────────────────────────────────── */

/**
 * Pulls a bounded sample of recent orders, runs each through the SHARED
 * evaluator using the SHARED buildOrderContext, and returns counts +
 * examples. No UI-side filtering. No approximations. Runtime execution
 * at order import will use the exact same evaluator and context builder.
 */
export const previewHoldCreationRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      tenantId: z.string().uuid(),
      scope: ScopeZ,
      condition_tree: ConditionZ,
      sampleSize: z.number().int().min(50).max(2000).default(500),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const tree = data.condition_tree as Condition;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = context.supabase as any;
    let q = sb
      .from("orders")
      .select("id, order_number, customer_id, created_at")
      .eq("tenant_id", data.tenantId)
      .order("created_at", { ascending: false })
      .limit(data.sampleSize);
    if (data.scope.scope_level === "customer") {
      q = q.eq("customer_id", data.scope.customer_id);
    }
    const { data: orders, error } = await q;
    if (error) throw new Error(`E_PREVIEW: ${error.message}`);

    const examples: Array<{ id: string; label: string; reason: string; href: string }> = [];
    let matchCount = 0;
    let evaluated = 0;
    for (const o of orders ?? []) {
      const ctx = await buildOrderContext(sb, "order.imported", o.id);
      if (!ctx) continue;
      evaluated++;
      if (evaluate(tree, ctx)) {
        matchCount++;
        if (examples.length < 5) {
          examples.push({
            id: o.id,
            label: o.order_number ?? o.id.slice(0, 8),
            reason: "Matches condition",
            href: `/app/orders/${o.id}`,
          });
        }
      }
    }

    return {
      sample_size: evaluated,
      matching_in_sample: matchCount,
      match_rate: evaluated > 0 ? matchCount / evaluated : 0,
      examples,
      footnotes: [
        `Evaluated ${evaluated} of the most recent orders${data.scope.scope_level === "customer" ? " for the selected customer" : ""}.`,
        "Preview uses the same evaluator and context builder that will run at order import.",
      ],
    };
  });

/* ── activate (atomic upsert via RPC) ─────────────────────────────────── */

const ActivateCreationZ = z.object({
  tenantId: z.string().uuid(),
  intentId: z.string().uuid(),
  prompt: z.string().min(1).max(2000),
  summary: z.string().min(1).max(600),
  source: z.enum(["ai_proposal", "ai_proposal_modified", "manual"]),
  scope: ScopeZ,
  rules: z.array(HoldCreationRuleZ).min(1).max(6),
});

export const activateHoldCreationIntent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ActivateCreationZ.parse(d))
  .handler(async ({ data, context }) => {
    // Defense-in-depth — re-validate every actions[] before opening the tx.
    for (const r of data.rules) validateActions(r.actions);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = context.supabase as any;
    const { data: count, error } = await sb.rpc("activate_hold_creation_intent", {
      p_tenant_id: data.tenantId,
      p_intent_id: data.intentId,
      p_intent_prompt: data.prompt,
      p_intent_summary: data.summary,
      p_source: data.source,
      p_scope_level: data.scope.scope_level,
      p_customer_id: data.scope.scope_level === "customer" ? data.scope.customer_id : null,
      p_rules: data.rules.map((r) => ({
        name: r.name,
        description: r.description ?? null,
        trigger_event: "order.imported",
        condition_tree: r.condition_tree,
        actions: r.actions,
        is_active: r.is_active,
        priority: r.priority,
      })),
    } as never);
    if (error) throw new Error(`E_ACTIVATE: ${error.message}`);

    await context.supabase.from("app_logs").insert({
      level: "info",
      source: "hold_creation",
      message: "hold_creation.activated",
      extras: {
        tenant_id: data.tenantId,
        intent_id: data.intentId,
        source: data.source,
        scope_level: data.scope.scope_level,
        rules_written: count ?? data.rules.length,
      },
    } as never);
    await recordOutcome(context.supabase, data.intentId, "activated", {
      source: data.source,
      rules_written: count ?? data.rules.length,
    });

    return { ok: true, rulesWritten: (count as number | null) ?? data.rules.length };
  });

/* ── delete a single rule ─────────────────────────────────────────────── */

const DeleteHCZ = z.object({ id: z.string().uuid() });

export const deleteHoldCreationRule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => DeleteHCZ.parse(d))
  .handler(async ({ data, context }) => {
    // Phase 3 dual-write: legacy delete + mirror delete in one Postgres txn.
    const { error } = await (context.supabase as any).rpc(
      "hold_creation_rule_delete_v1",
      { _id: data.id },
    );
    if (error) throw new Error(`E_DELETE: ${error.message}`);
    return { ok: true };
  });